import {
  FILTER_COMPOSITIONS,
  FILTER_OPERATORS,
  FILTER_OPERATORS_WITHOUT_VALUE,
  isFilterTree,
  type AudienceFieldDescriptor,
  type FilterCondition,
  type FilterNode,
  type FilterOperator,
  type FilterTree,
} from '@ferrari/types';
import {
  ROTULO_DA_COMPOSICAO,
  ROTULO_DO_OPERADOR,
  acrescentarEm,
  condicaoNova,
  grupoInicial,
  interpretarValor,
  removerEm,
  substituirEm,
  textoDoValor,
  trocarOperador,
  type Caminho,
} from '../lib/filtro';

/**
 * O Filter Builder do Console SPEC v1.5 §182.
 *
 * Os operadores e as composicoes vem do PACOTE COMPARTILHADO, e nao de uma
 * lista escrita aqui. E a mesma razao pela qual o vocabulario mora lá: duas
 * listas divergem, e o sintoma seria a tela oferecer um operador que o
 * avaliador do servidor ignora, devolvendo um total menor sem nenhum aviso.
 *
 * Os campos vem da FONTE (§181 GET AVAILABLE FIELDS), e somente os marcados
 * como filtraveis aparecem. Campo que a fonte nao sabe filtrar viraria uma
 * condicao que nunca casa ninguem, e zero parece resposta.
 */

const CHAVE_DE_CAMINHO = (caminho: Caminho) => (caminho.length === 0 ? 'raiz' : caminho.join('-'));

export function FilterBuilder({
  filtro,
  campos,
  onChange,
}: {
  filtro: FilterNode;
  campos: readonly AudienceFieldDescriptor[];
  onChange: (proximo: FilterNode) => void;
}) {
  const filtraveis = campos.filter((c) => c.filterable);

  if (filtraveis.length === 0) {
    return (
      <p className="rounded border border-line bg-surface-sunken p-4 text-sm text-ink-muted">
        Esta fonte não declarou nenhum campo que possa ser usado em filtro. Sem campos declarados
        não há o que oferecer aqui, e montar uma condição sobre um nome que a fonte não reconhece
        traria zero registros sem explicação. Configure os campos da fonte antes de montar o
        segmento.
      </p>
    );
  }

  return (
    <No
      no={filtro}
      caminho={[]}
      campos={filtraveis}
      raiz={filtro}
      onChange={onChange}
      podeRemover={false}
    />
  );
}

function No({
  no,
  caminho,
  campos,
  raiz,
  onChange,
  podeRemover,
}: {
  no: FilterNode;
  caminho: Caminho;
  campos: readonly AudienceFieldDescriptor[];
  raiz: FilterNode;
  onChange: (proximo: FilterNode) => void;
  podeRemover: boolean;
}) {
  if (isFilterTree(no)) {
    return (
      <Grupo
        grupo={no}
        caminho={caminho}
        campos={campos}
        raiz={raiz}
        onChange={onChange}
        podeRemover={podeRemover}
      />
    );
  }
  return (
    <Condicao
      condicao={no}
      caminho={caminho}
      campos={campos}
      raiz={raiz}
      onChange={onChange}
      podeRemover={podeRemover}
    />
  );
}

function Grupo({
  grupo,
  caminho,
  campos,
  raiz,
  onChange,
  podeRemover,
}: {
  grupo: FilterTree;
  caminho: Caminho;
  campos: readonly AudienceFieldDescriptor[];
  raiz: FilterNode;
  onChange: (proximo: FilterNode) => void;
  podeRemover: boolean;
}) {
  const chave = CHAVE_DE_CAMINHO(caminho);
  const primeiroCampo = campos[0]!.name;

  return (
    <fieldset className="rounded border border-line-strong p-3">
      <legend className="px-1 text-xs text-ink-muted">
        Grupo: precisa atender {ROTULO_DA_COMPOSICAO[grupo.operator]}
      </legend>

      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`composicao-${chave}`} className="text-xs text-ink-muted">
          Precisa atender
        </label>
        <select
          id={`composicao-${chave}`}
          value={grupo.operator}
          onChange={(e) =>
            onChange(
              substituirEm(raiz, caminho, {
                ...grupo,
                operator: e.target.value as FilterTree['operator'],
              }),
            )
          }
          className="rounded border border-line px-2 py-1 text-sm"
        >
          {FILTER_COMPOSITIONS.map((c) => (
            <option key={c} value={c}>
              {ROTULO_DA_COMPOSICAO[c]}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => onChange(acrescentarEm(raiz, caminho, condicaoNova(primeiroCampo)))}
          className="rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised"
        >
          Adicionar condição
        </button>
        <button
          type="button"
          onClick={() => onChange(acrescentarEm(raiz, caminho, grupoInicial(primeiroCampo)))}
          className="rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised"
        >
          Adicionar grupo
        </button>
        {podeRemover ? (
          <button
            type="button"
            onClick={() => onChange(removerEm(raiz, caminho))}
            className="rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised"
          >
            Remover grupo
          </button>
        ) : null}
      </div>

      {grupo.children.length === 0 ? (
        <p className="mt-2 text-xs text-danger-ink">
          Este grupo está sem condição. Acrescente uma condição ou remova o grupo: um grupo vazio
          não seleciona ninguém.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {grupo.children.map((filho, i) => (
            <li key={CHAVE_DE_CAMINHO([...caminho, i])}>
              <No
                no={filho}
                caminho={[...caminho, i]}
                campos={campos}
                raiz={raiz}
                onChange={onChange}
                podeRemover
              />
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

function Condicao({
  condicao,
  caminho,
  campos,
  raiz,
  onChange,
  podeRemover,
}: {
  condicao: FilterCondition;
  caminho: Caminho;
  campos: readonly AudienceFieldDescriptor[];
  raiz: FilterNode;
  onChange: (proximo: FilterNode) => void;
  podeRemover: boolean;
}) {
  const chave = CHAVE_DE_CAMINHO(caminho);
  const semValor = FILTER_OPERATORS_WITHOUT_VALUE.includes(condicao.operator);

  return (
    <div className="flex flex-wrap items-end gap-2 rounded border border-line p-2">
      <div>
        <label htmlFor={`campo-${chave}`} className="block text-xs text-ink-muted">
          Campo
        </label>
        <select
          id={`campo-${chave}`}
          value={condicao.field}
          onChange={(e) =>
            onChange(substituirEm(raiz, caminho, { ...condicao, field: e.target.value }))
          }
          className="mt-1 rounded border border-line px-2 py-1 text-sm"
        >
          {campos.map((c) => (
            <option key={c.name} value={c.name}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`operador-${chave}`} className="block text-xs text-ink-muted">
          Comparação
        </label>
        <select
          id={`operador-${chave}`}
          value={condicao.operator}
          onChange={(e) => {
            const operador = e.target.value as FilterOperator;
            // A troca passa por `trocarOperador`, que tira o valor de
            // "está vazio" e devolve um valor vazio ao sair dele. Sem isso o
            // servidor recusaria com VALOR_PROIBIDO ou VALOR_AUSENTE por um
            // clique a mais.
            onChange(substituirEm(raiz, caminho, trocarOperador(condicao, operador)));
          }}
          className="mt-1 rounded border border-line px-2 py-1 text-sm"
        >
          {FILTER_OPERATORS.map((op) => (
            <option key={op} value={op}>
              {ROTULO_DO_OPERADOR[op]}
            </option>
          ))}
        </select>
      </div>

      {semValor ? null : (
        <div className="grow">
          <label htmlFor={`valor-${chave}`} className="block text-xs text-ink-muted">
            Valor
          </label>
          <input
            id={`valor-${chave}`}
            value={textoDoValor(condicao)}
            autoComplete="off"
            onChange={(e) =>
              onChange(
                substituirEm(raiz, caminho, {
                  ...condicao,
                  value: interpretarValor(e.target.value, condicao.operator),
                }),
              )
            }
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
          />
          {condicao.operator === 'IN' || condicao.operator === 'NOT_IN' ? (
            <p className="mt-1 text-xs text-ink-faint">Separe os valores por vírgula.</p>
          ) : null}
          {condicao.operator === 'BETWEEN' ? (
            <p className="mt-1 text-xs text-ink-faint">
              Dois valores separados por vírgula, do menor para o maior.
            </p>
          ) : null}
        </div>
      )}

      {podeRemover ? (
        <button
          type="button"
          onClick={() => onChange(removerEm(raiz, caminho))}
          className="rounded border border-line px-2 py-1 text-xs hover:bg-surface-raised"
        >
          Remover condição
        </button>
      ) : null}
    </div>
  );
}
